import { useEffect, useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Save, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import useDriverCharges from "../../Sevices/Driver Api/useDriverCharges";
import DriverDropdown from "../Utils/DriverDropdown";

const INITIAL_CHARGES = {
  per_day: {
    id: null,
    amount: "",
  },
  per_order: {
    id: null,
    amount: "",
  },
};

export default function DriverChargesModal({
  open,
  onOpenChange,
  adminId,
  drivers = [],
  initialDriverCode = "",
  onSuccess,
}) {
  const { getDriverChargesApi, saveDriverChargeApi } = useDriverCharges();

  const [driverCode, setDriverCode] = useState("");

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [existing, setExisting] = useState(INITIAL_CHARGES);

  const normalizeCode = (value) => String(value ?? "").trim();

  const selectedDriver = useMemo(() => {
    const selectedCode = normalizeCode(driverCode);

    if (!selectedCode) {
      return null;
    }

    return (
      drivers.find((driver) => {
        const possibleCodes = [
          driver?.driverCode,
          driver?.driverId,
          driver?.driver_code,
          driver?.driver_id,
        ]
          .filter((value) => value !== null && value !== undefined)
          .map(normalizeCode);

        return possibleCodes.includes(selectedCode);
      }) || null
    );
  }, [drivers, driverCode]);

  const reset = () => {
    setDriverCode("");
    setExisting(INITIAL_CHARGES);
    setLoading(false);
    setSaving(false);
  };

  const normalizeChargeResponse = (response) => {
    /*
      Supported formats:

      1.
      [
        {
          driverCode: "PD000001",
          charges: [...]
        }
      ]

      2.
      {
        data: [...]
      }

      3.
      {
        response: [...]
      }

      4.
      Direct charge object
    */

    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.data)) {
      return response.data;
    }

    if (Array.isArray(response?.response)) {
      return response.response;
    }

    if (Array.isArray(response?.result)) {
      return response.result;
    }

    if (response && typeof response === "object") {
      return [response];
    }

    return [];
  };

  const findDriverChargeGroup = (rows, code) => {
    const selectedCode = normalizeCode(code);

    return (
      rows.find((row) => {
        const rowCode = normalizeCode(
          row?.driverCode ||
            row?.driver_code ||
            row?.driverId ||
            row?.driver_id,
        );

        return rowCode === selectedCode;
      }) ||
      rows[0] ||
      null
    );
  };

  const mapChargesToForm = (response, code) => {
    const rows = normalizeChargeResponse(response);

    const group = findDriverChargeGroup(rows, code);

    if (!group) {
      return INITIAL_CHARGES;
    }

    const charges = Array.isArray(group?.charges) ? group.charges : [];

    /*
      API kabhi single object me dono amount bhejti hai.
    */
    const directCharge = charges[0] || group?.charge || group || {};

    /*
      API alag-alag type rows bheje to unko bhi handle karega.
    */
    const perDayRow =
      charges.find((charge) => {
        const type = String(
          charge?.type || charge?.chargeType || charge?.charge_type || "",
        )
          .trim()
          .toLowerCase();

        return ["per_day", "perday", "day"].includes(type);
      }) || directCharge;

    const perOrderRow =
      charges.find((charge) => {
        const type = String(
          charge?.type || charge?.chargeType || charge?.charge_type || "",
        )
          .trim()
          .toLowerCase();

        return ["per_order", "perorder", "order"].includes(type);
      }) || directCharge;

    const perDayAmount =
      perDayRow?.perDayAmount ??
      perDayRow?.per_day_amount ??
      perDayRow?.dailyAmount ??
      perDayRow?.daily_amount ??
      (String(perDayRow?.type || perDayRow?.chargeType || "")
        .toLowerCase()
        .includes("day")
        ? perDayRow?.amount
        : "");

    const perOrderAmount =
      perOrderRow?.perOrderAmount ??
      perOrderRow?.per_order_amount ??
      perOrderRow?.orderAmount ??
      perOrderRow?.order_amount ??
      (String(perOrderRow?.type || perOrderRow?.chargeType || "")
        .toLowerCase()
        .includes("order")
        ? perOrderRow?.amount
        : "");

    return {
      per_day: {
        id:
          perDayRow?.id ??
          perDayRow?.chargeId ??
          perDayRow?.charge_id ??
          directCharge?.id ??
          null,

        amount:
          perDayAmount === null || perDayAmount === undefined
            ? ""
            : String(perDayAmount),
      },

      per_order: {
        id:
          perOrderRow?.id ??
          perOrderRow?.chargeId ??
          perOrderRow?.charge_id ??
          directCharge?.id ??
          null,

        amount:
          perOrderAmount === null || perOrderAmount === undefined
            ? ""
            : String(perOrderAmount),
      },
    };
  };

  const fetchChargesForDriver = async (code) => {
    const normalizedCode = normalizeCode(code);

    if (!normalizedCode) {
      setExisting(INITIAL_CHARGES);
      return;
    }

    setLoading(true);

    try {
      const response = await getDriverChargesApi(normalizedCode);

      const mappedCharges = mapChargesToForm(response, normalizedCode);

      setExisting(mappedCharges);
    } catch (error) {
      console.error("fetchChargesForDriver error:", error);

      setExisting(INITIAL_CHARGES);

      toast.error(
        error?.response?.data?.responseMessage ||
          "Error fetching driver charges",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      reset();
      return;
    }

    const code = normalizeCode(initialDriverCode);

    if (code) {
      setDriverCode(code);
      fetchChargesForDriver(code);
    } else {
      setDriverCode("");
      setExisting(INITIAL_CHARGES);
    }
  }, [open, initialDriverCode]);

  const onDriverChange = async (value) => {
    const code = normalizeCode(value);

    setDriverCode(code);
    setExisting(INITIAL_CHARGES);

    if (code) {
      await fetchChargesForDriver(code);
    }
  };

  const validateAmount = (value) => {
    const normalized = String(value ?? "").trim();

    if (!normalized) {
      return false;
    }

    return /^\d+(\.\d+)?$/.test(normalized);
  };

  const handleSave = async () => {
    if (saving) return;

    const normalizedDriverCode = normalizeCode(driverCode);

    if (!normalizedDriverCode) {
      toast.error("Please select a driver");
      return;
    }

    const perDayAmount = String(existing?.per_day?.amount ?? "").trim();

    const perOrderAmount = String(existing?.per_order?.amount ?? "").trim();

    if (!validateAmount(perDayAmount)) {
      toast.error("Per-day amount must be numeric");
      return;
    }

    if (!validateAmount(perOrderAmount)) {
      toast.error("Per-order amount must be numeric");
      return;
    }

    setSaving(true);

    try {
      const result = await saveDriverChargeApi({
        adminId: String(adminId || ""),
        driverCode: normalizedDriverCode,
        type1: "per_day",
        type1Amount: perDayAmount,
        type2: "per_order",
        type2Amount: perOrderAmount,
      });

      /*
        Hook normalized result return karta hai:

        {
          success: true/false,
          data: API response,
          message: "..."
        }

        Toast hook me already handle ho chuka hai.
        Modal me koi second toast nahi lagega.
      */
      if (!result?.success) {
        return;
      }

      if (typeof onSuccess === "function") {
        await onSuccess(result?.data);
      }

      onOpenChange?.(false);
      reset();
    } catch (error) {
      /*
        Normally hook error ko catch karta hai.
        Ye fallback unexpected JS error ke liye hai.
      */
      console.error("Driver charge save error:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (saving) return;

        if (!nextOpen) {
          reset();
        }

        onOpenChange?.(nextOpen);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />

        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[92%] max-w-xl -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl bg-white shadow-lg">
          <div className="flex items-center justify-between border-b bg-gradient-to-r from-orange-50 to-white px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-orange-100 text-orange-700">
                <Wallet size={18} />
              </div>

              <div>
                <Dialog.Title className="text-base font-semibold text-gray-900">
                  Driver Charges
                </Dialog.Title>

                <p className="text-xs text-gray-600">
                  Set per-day and per-order charges
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              disabled={saving}
              className="rounded-lg p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>

          <div className="space-y-4 px-5 py-4">
            <div>
              <label
                htmlFor="driverCode"
                className="mb-1 block text-xs font-semibold text-gray-700"
              >
                Select Driver
              </label>

              <DriverDropdown
                drivers={(drivers || []).map((driver) => ({
                  driverId:
                    driver.driverCode ||
                    driver.driverId ||
                    driver.driver_code ||
                    driver.driver_id,
                  fullName: `${
                    driver.fullName || driver.username || driver.name || "-"
                  } • ${driver.teamName || driver.team_name || "-"}`,
                }))}
                value={driverCode}
                onChange={onDriverChange}
                placeholder="-- Select --"
                loading={saving || loading}
                className="w-full"
                dropdownHeight="max-h-[172px]"
              />

              {selectedDriver && (
                <div className="mt-2 text-xs text-gray-600">
                  <b>
                    {selectedDriver?.fullName ||
                      selectedDriver?.username ||
                      selectedDriver?.name ||
                      "-"}
                  </b>
                  {" • "}
                  {selectedDriver?.phone || "-"}
                  {" • "}
                  {selectedDriver?.teamName || selectedDriver?.team_name || "-"}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <ChargesCard
                title="Per Day"
                type="per_day"
                value={existing.per_day.amount}
                setValue={(value) =>
                  setExisting((previous) => ({
                    ...previous,
                    per_day: {
                      ...previous.per_day,
                      amount: value,
                    },
                  }))
                }
                loading={loading}
                disabled={saving}
              />

              <ChargesCard
                title="Per Order"
                type="per_order"
                value={existing.per_order.amount}
                setValue={(value) =>
                  setExisting((previous) => ({
                    ...previous,
                    per_order: {
                      ...previous.per_order,
                      amount: value,
                    },
                  }))
                }
                loading={loading}
                disabled={saving}
              />
            </div>

            <div className="rounded-lg border p-3 text-xs text-gray-600">
              {loading
                ? "Fetching existing charges..."
                : driverCode
                  ? "Existing charges are prefilled. Change the values and save."
                  : "Select a driver to begin."}
            </div>
          </div>

          <div className="flex items-center justify-between border-t px-5 py-4">
            <button
              type="button"
              className="rounded bg-gray-200 px-3 py-2 text-sm hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => onOpenChange?.(false)}
              disabled={saving}
            >
              Close
            </button>

            <button
              type="button"
              className="inline-flex min-w-[110px] items-center justify-center gap-2 rounded bg-orange-600 px-3 py-2 text-sm text-white hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleSave}
              disabled={saving || loading || !driverCode}
            >
              {saving ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save
                </>
              )}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ChargesCard({ title, type, value, setValue, loading, disabled }) {
  return (
    <div className="rounded-xl border bg-white p-4">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-gray-700">{title}</div>

        <span className="rounded-full bg-gray-100 px-2 py-1 text-[11px] text-gray-700">
          {type}
        </span>
      </div>

      <div className="mt-3">
        <label className="text-[11px] text-gray-500">Amount</label>

        <input
          type="text"
          inputMode="decimal"
          className="mt-1 w-full rounded border px-3 py-2 text-sm shadow-sm outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-gray-100"
          value={value}
          onChange={(event) => {
            const nextValue = event.target.value;

            if (nextValue === "" || /^\d*\.?\d*$/.test(nextValue)) {
              setValue(nextValue);
            }
          }}
          placeholder="Enter amount"
          disabled={loading || disabled}
          autoComplete="off"
        />
      </div>
    </div>
  );
}
